import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

const AdminLogin = ({ auth }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      if (response.data.user.role !== 'admin') {
        toast.error('Admin access required');
        setLoading(false);
        return;
      }
      auth.login(response.data.access_token, response.data.user);
      toast.success('Welcome back!');
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <span className="font-heading text-3xl tracking-tight text-white">
            CRAWL<span className="text-[#FF2D78]">BABY</span>CRAWL
          </span>
          <p className="text-gray-400 mt-2">Admin Portal</p>
        </div>

        <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-8">
          <div className="flex items-center gap-3 mb-6">
            <Lock className="text-[#FF2D78]" size={24} />
            <h1 className="font-heading text-2xl text-white uppercase">Admin Login</h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label className="text-gray-400">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="admin-email-input"
                  className="pl-10 bg-white/5 border-white/10 text-white"
                  placeholder="admin@crawlbabycrawl.com"
                />
              </div>
            </div>

            <div>
              <Label className="text-gray-400">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  data-testid="admin-password-input"
                  className="pl-10 bg-white/5 border-white/10 text-white"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="admin-login-btn"
              className="w-full bg-[#FF2D78] hover:bg-[#ff4d8d] text-white font-bold uppercase tracking-widest py-6"
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
          </form>

          <p className="text-gray-500 text-sm text-center mt-6">
            Demo: admin@crawlbabycrawl.com / admin123
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
